/**
 * sl-hle4.9: Custom Apps — Bandwidth & Traffic section
 *
 * Pro+ workspace traffic dashboard. Shows month-to-date GB served + remaining
 * capacity, and lets owners set an opt-in monthly bandwidth budget (block /
 * warn behavior, alert thresholds).
 *
 * The parent tab is plan-gated on FEATURE_KEY_CUSTOM_APPS_CREATE
 * (sl-hle4.8 — Free workspaces never see this surface), so the section
 * itself mirrors backend RBAC: Owners can read usage and mutate budgets,
 * Admins can inspect the stored budget row, Members do not hit billing APIs.
 *
 * SOLID:
 *   - S: Bandwidth + budget UI only — no broader workspace config concerns.
 *   - D: Depends on GraphQLService + WorkspaceService + GuardService
 *     abstractions; backend ownership of budget math is preserved.
 */
import {
  Button,
  Input,
  Menu,
  MenuItem,
  notify,
  Skeleton,
  Switch,
} from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { GraphQLService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import {
  clearCustomAppBandwidthBudgetMutation,
  currentBandwidthUsageQuery,
  customAppBandwidthBudgetQuery,
  customAppInvocationStatusQuery,
  customAppsQuery,
  setCustomAppBandwidthBudgetMutation,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type FC, useEffect, useMemo, useState } from 'react';

import {
  canManageCustomAppBandwidthBudget,
  canReadCustomAppBandwidthBudget,
  shouldLoadCustomAppCurrentBandwidthUsage,
} from './custom-app-bandwidth-access';

const DEFAULT_THRESHOLDS = [60, 80, 100];

interface UsageState {
  customAppId: string;
  usedGB: number;
  remainingGB: number;
  asOfDate: Date;
  resetDate: Date;
  budgetSet: boolean;
  budgetMonthlyLimitMicro: bigint | null;
  budgetUsedPct: number | null;
  budgetBehavior: 'block' | 'warn' | null;
  currentTier: string;
}

interface BudgetState {
  monthlyLimitMicro: bigint;
  alertThresholds: number[];
  behavior: 'block' | 'warn';
}

interface InvocationActionStatus {
  actionId: string;
  label: string;
  operation: string;
  allowed: boolean;
  policyReason: string;
  effectiveRatePerHour: number;
  usedLastHour: number;
  concurrencyLimit: number;
  concurrencyUsed: number;
}

interface InvocationStatus {
  planSlug: string;
  exposure: string;
  origin: string;
  actions: InvocationActionStatus[];
}

interface ViewListItem {
  id: string;
  title: string | null;
  slug: string;
  isPublished: boolean;
}

function bigintFromMaybeString(
  value: string | null | undefined
): bigint | null {
  if (value == null) return null;
  return BigInt(value);
}

function microToCredits(micro: bigint): number {
  // 1 credit = 10_000 micro
  return Number(micro / 1_000n) / 10;
}

function creditsToMicro(credits: number): bigint {
  return BigInt(Math.round(credits * 10_000));
}

function fmtCredits(credits: number): string {
  return credits.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function fmtGB(gb: number): string {
  return gb.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export const BandwidthSection: FC = () => {
  const t = useI18n();
  const gqlService = useService(GraphQLService);
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const information = useWorkspaceInfo(workspaceService.workspace);
  const canReadBudget = canReadCustomAppBandwidthBudget(information);
  const canManageBudget = canManageCustomAppBandwidthBudget(information);
  const canLoadUsage = shouldLoadCustomAppCurrentBandwidthUsage(information);

  const [usage, setUsage] = useState<UsageState | null>(null);
  const [budget, setBudget] = useState<BudgetState | null>(null);
  const [invocationStatus, setInvocationStatus] =
    useState<InvocationStatus | null>(null);
  const [views, setViews] = useState<ViewListItem[] | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Local edit state — owned by the form, not the server. Reset on reload.
  const [draftEnabled, setDraftEnabled] = useState(false);
  const [draftLimitCredits, setDraftLimitCredits] = useState('');
  const [draftBehavior, setDraftBehavior] = useState<'block' | 'warn'>('block');
  const [draftThresholds, setDraftThresholds] =
    useState<number[]>(DEFAULT_THRESHOLDS);

  const loadViews = useAsyncCallback(async () => {
    try {
      const result = await gqlService.gql({
        query: customAppsQuery,
        variables: { workspaceId },
      });
      const list = result.customApps
        .filter(v => v.isPublished)
        .map(v => ({
          id: v.id,
          title: v.title,
          slug: v.slug,
          isPublished: v.isPublished,
        }));
      setViews(list);
      if (list.length > 0 && !selectedViewId) {
        setSelectedViewId(list[0].id);
      } else if (
        selectedViewId &&
        !list.some(view => view.id === selectedViewId)
      ) {
        setSelectedViewId(list[0]?.id ?? null);
      }
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
      setViews([]);
      setSelectedViewId(null);
    }
  }, [gqlService, workspaceId, selectedViewId]);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  const refresh = useAsyncCallback(async () => {
    if (!selectedViewId || !canReadBudget) {
      setUsage(null);
      setBudget(null);
      setInvocationStatus(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const budgetRequest = gqlService.gql({
        query: customAppBandwidthBudgetQuery,
        variables: { workspaceId, customAppId: selectedViewId },
      });
      const usageRequest = canLoadUsage
        ? gqlService.gql({
            query: currentBandwidthUsageQuery,
            variables: { workspaceId, customAppId: selectedViewId },
          })
        : null;
      const invocationRequest = canLoadUsage
        ? gqlService.gql({
            query: customAppInvocationStatusQuery,
            variables: { workspaceId, customAppId: selectedViewId },
          })
        : null;
      const [budgetRes, usageRes, invocationRes] = await Promise.all([
        budgetRequest,
        usageRequest,
        invocationRequest,
      ]);
      if (usageRes) {
        const u = usageRes.currentBandwidthUsage;
        setUsage({
          customAppId: u.customAppId,
          usedGB: u.usedGB,
          remainingGB: u.remainingGB,
          asOfDate: new Date(u.asOfDate),
          resetDate: new Date(u.resetDate),
          budgetSet: u.budgetSet,
          budgetMonthlyLimitMicro: bigintFromMaybeString(
            u.budgetMonthlyLimitMicro
          ),
          budgetUsedPct: u.budgetUsedPct ?? null,
          budgetBehavior: (u.budgetBehavior as 'block' | 'warn' | null) ?? null,
          currentTier: u.currentTier,
        });
      } else {
        setUsage(null);
      }
      setInvocationStatus(
        invocationRes?.customAppInvocationStatus
          ? {
              ...invocationRes.customAppInvocationStatus,
              actions: [...invocationRes.customAppInvocationStatus.actions],
            }
          : null
      );
      const b = budgetRes.customAppBandwidthBudget;
      if (b) {
        const limitMicro = BigInt(b.monthlyLimitMicro);
        const next: BudgetState = {
          monthlyLimitMicro: limitMicro,
          alertThresholds: [...b.alertThresholds].sort((a, c) => a - c),
          behavior: b.behavior === 'warn' ? 'warn' : 'block',
        };
        setBudget(next);
        setDraftEnabled(true);
        setDraftLimitCredits(microToCredits(limitMicro).toString());
        setDraftBehavior(next.behavior);
        setDraftThresholds(next.alertThresholds);
      } else {
        setBudget(null);
        setDraftEnabled(false);
        setDraftLimitCredits('');
        setDraftBehavior('block');
        setDraftThresholds(DEFAULT_THRESHOLDS);
      }
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setIsLoading(false);
    }
  }, [gqlService, workspaceId, selectedViewId, canReadBudget, canLoadUsage]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSave = useAsyncCallback(async () => {
    if (!canManageBudget || !selectedViewId) return;
    setIsSaving(true);
    try {
      if (!draftEnabled) {
        await gqlService.gql({
          query: clearCustomAppBandwidthBudgetMutation,
          variables: { workspaceId, customAppId: selectedViewId },
        });
        notify.success({
          title:
            t[
              'com.streamlain.settings.workspace.customApps.bandwidth.budget.clearedToast'
            ](),
        });
      } else {
        const limitCredits = Number(draftLimitCredits);
        if (!Number.isFinite(limitCredits) || limitCredits <= 0) {
          notify.error({
            title:
              t[
                'com.streamlain.settings.workspace.customApps.bandwidth.budget.invalidLimit'
              ](),
          });
          return;
        }
        await gqlService.gql({
          query: setCustomAppBandwidthBudgetMutation,
          variables: {
            input: {
              workspaceId,
              customAppId: selectedViewId,
              monthlyLimitMicro: creditsToMicro(limitCredits).toString(),
              alertThresholds: draftThresholds,
              behavior: draftBehavior,
            },
          },
        });
        notify.success({
          title:
            t[
              'com.streamlain.settings.workspace.customApps.bandwidth.budget.savedToast'
            ](),
        });
      }
      refresh();
    } catch (err) {
      notify.error({ error: UserFriendlyError.fromAny(err) });
    } finally {
      setIsSaving(false);
    }
  }, [
    gqlService,
    workspaceId,
    selectedViewId,
    canManageBudget,
    draftEnabled,
    draftLimitCredits,
    draftBehavior,
    draftThresholds,
    refresh,
    t,
  ]);

  const toggleThreshold = (threshold: number) => {
    setDraftThresholds(prev => {
      const hasThreshold = prev.includes(threshold);
      const next = hasThreshold
        ? prev.filter(value => value !== threshold)
        : [...prev, threshold];
      return next.sort((a, b) => a - b);
    });
  };

  const usageView = useMemo(() => {
    if (!usage) return null;
    return {
      usedGB: fmtGB(usage.usedGB),
      remainingGB: fmtGB(usage.remainingGB),
      asOfDate: usage.asOfDate.toLocaleString(),
      resetDate: usage.resetDate.toLocaleDateString(),
      tier: usage.currentTier,
      budgetUsedPct: usage.budgetUsedPct,
    };
  }, [usage]);

  if (isLoading && !usage) {
    return (
      <SettingWrapper
        title={t[
          'com.streamlain.settings.workspace.customApps.bandwidth.title'
        ]()}
      >
        <Skeleton height={120} />
      </SettingWrapper>
    );
  }

  return (
    <SettingWrapper
      title={t[
        'com.streamlain.settings.workspace.customApps.bandwidth.title'
      ]()}
    >
      {views && views.length > 1 ? (
        <SettingRow
          name={
            t[
              'com.streamlain.settings.workspace.customApps.bandwidth.selector.label'
            ]?.() ?? 'Site'
          }
          desc={
            t[
              'com.streamlain.settings.workspace.customApps.bandwidth.selector.desc'
            ]?.() ?? 'Traffic budget applies to the selected custom app.'
          }
        >
          <Menu
            items={views.map(view => (
              <MenuItem
                key={view.id}
                selected={view.id === selectedViewId}
                onClick={() => setSelectedViewId(view.id)}
              >
                {view.title?.length ? view.title : view.slug}
              </MenuItem>
            ))}
          >
            <Button>
              {views.find(view => view.id === selectedViewId)?.title ||
                views.find(view => view.id === selectedViewId)?.slug ||
                '—'}
            </Button>
          </Menu>
        </SettingRow>
      ) : null}

      {views && views.length === 0 ? (
        <SettingRow
          name={
            t[
              'com.streamlain.settings.workspace.customApps.bandwidth.empty.title'
            ]?.() ?? 'No custom apps'
          }
          desc={
            t[
              'com.streamlain.settings.workspace.customApps.bandwidth.empty.desc'
            ]?.() ?? 'Publish a custom app before setting a traffic budget.'
          }
        />
      ) : null}

      {usageView ? (
        <>
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.bandwidth.usage.usedLabel'
            ]()}
            desc={
              t[
                'com.streamlain.settings.workspace.customApps.bandwidth.usage.usedDesc'
              ]?.({
                used: usageView.usedGB,
                remaining: usageView.remainingGB,
              }) ??
              `${usageView.usedGB} GB used · ${usageView.remainingGB} GB remaining`
            }
          />
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.bandwidth.usage.tierLabel'
            ]()}
            desc={usageView.tier}
          />
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.bandwidth.usage.asOfLabel'
            ]()}
            desc={
              t[
                'com.streamlain.settings.workspace.customApps.bandwidth.usage.asOfDesc'
              ]?.({
                asOf: usageView.asOfDate,
                resets: usageView.resetDate,
              }) ?? `${usageView.asOfDate} · resets ${usageView.resetDate}`
            }
          />
        </>
      ) : null}

      {invocationStatus ? (
        <>
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.invocation.title'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.invocation.summary'
            ]({
              plan: invocationStatus.planSlug,
              exposure: invocationStatus.exposure,
              origin: invocationStatus.origin,
            })}
          />
          {invocationStatus.actions.map(action => (
            <SettingRow
              key={action.actionId}
              name={action.label}
              desc={
                action.allowed
                  ? t[
                      'com.streamlain.settings.workspace.customApps.invocation.actionAllowed'
                    ]({
                      used: String(action.usedLastHour),
                      limit: String(action.effectiveRatePerHour),
                      concurrencyUsed: String(action.concurrencyUsed),
                      concurrencyLimit: String(action.concurrencyLimit),
                    })
                  : t[
                      'com.streamlain.settings.workspace.customApps.invocation.actionBlocked'
                    ]({ reason: action.policyReason })
              }
            />
          ))}
        </>
      ) : null}

      <SettingRow
        name={t[
          'com.streamlain.settings.workspace.customApps.bandwidth.budget.toggleLabel'
        ]()}
        desc={t[
          'com.streamlain.settings.workspace.customApps.bandwidth.budget.toggleDesc'
        ]()}
      >
        <Switch
          data-testid="bandwidth-budget-enabled-toggle"
          checked={draftEnabled}
          disabled={!canManageBudget || isSaving}
          onChange={setDraftEnabled}
        />
      </SettingRow>

      {draftEnabled ? (
        <>
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.bandwidth.budget.limitLabel'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.customApps.bandwidth.budget.limitDesc'
            ]()}
          >
            <Input
              data-testid="bandwidth-budget-limit-input"
              type="number"
              value={draftLimitCredits}
              onChange={setDraftLimitCredits}
              disabled={!canManageBudget || isSaving}
              placeholder="0"
              style={{ maxWidth: 200 }}
            />
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.bandwidth.budget.behaviorLabel'
            ]()}
            desc={
              draftBehavior === 'block'
                ? t[
                    'com.streamlain.settings.workspace.customApps.bandwidth.budget.behavior.blockDesc'
                  ]()
                : t[
                    'com.streamlain.settings.workspace.customApps.bandwidth.budget.behavior.warnDesc'
                  ]()
            }
          >
            <Switch
              data-testid="bandwidth-budget-behavior-toggle"
              checked={draftBehavior === 'block'}
              disabled={!canManageBudget || isSaving}
              onChange={checked => setDraftBehavior(checked ? 'block' : 'warn')}
            />
          </SettingRow>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.customApps.bandwidth.budget.thresholdsLabel'
            ]()}
            desc={draftThresholds.map(p => `${p}%`).join(', ')}
            spreadCol={false}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {DEFAULT_THRESHOLDS.map(threshold => {
                const checked = draftThresholds.includes(threshold);
                return (
                  <Button
                    key={threshold}
                    variant={checked ? 'primary' : 'secondary'}
                    disabled={!canManageBudget || isSaving}
                    onClick={() => toggleThreshold(threshold)}
                  >
                    {threshold}%
                  </Button>
                );
              })}
            </div>
          </SettingRow>

          {usage?.budgetUsedPct != null ? (
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.bandwidth.budget.consumedLabel'
              ]()}
              desc={`${usage.budgetUsedPct.toFixed(1)}%`}
            />
          ) : null}

          {budget ? (
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.customApps.bandwidth.budget.serverLimitLabel'
              ]()}
              desc={`${fmtCredits(microToCredits(budget.monthlyLimitMicro))} credits`}
            />
          ) : null}
        </>
      ) : null}

      {canManageBudget ? (
        <SettingRow name="" desc="">
          <Button
            data-testid="bandwidth-budget-save"
            variant="primary"
            disabled={isSaving}
            onClick={handleSave}
          >
            {isSaving
              ? t[
                  'com.streamlain.settings.workspace.customApps.bandwidth.budget.savingAction'
                ]()
              : draftEnabled
                ? t[
                    'com.streamlain.settings.workspace.customApps.bandwidth.budget.saveAction'
                  ]()
                : t[
                    'com.streamlain.settings.workspace.customApps.bandwidth.budget.clearAction'
                  ]()}
          </Button>
        </SettingRow>
      ) : null}
    </SettingWrapper>
  );
};
